import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { API, formatErr, Field, inputCls } from './shared';
import SaleFields, { saleError, saleFormFrom, salePayload } from './SaleFields';

const UNIT_OPTIONS = ['sq ft', 'rolls', 'pcs', 'nos', 'set', 'litres', 'bottles', 'metres', 'kg'];
const defaultUnitForCategory = (cat) => (cat === 'ppf' || cat === 'sunfilm') ? 'sq ft' : 'pcs';

const ItemDialog = ({ item, activeCat, sections = [], onClose, onSaved, authHeader }) => {
  const isEdit = Boolean(item);
  const [form, setForm] = useState(() => ({
    name: item?.name || '',
    category: item?.category || activeCat,
    sku: item?.sku || '',
    quantity: item?.quantity ?? 0,
    unit: item?.unit || defaultUnitForCategory(item?.category || activeCat),
    safety_stock: item?.safety_stock ?? 0,
    unit_cost: item?.unit_cost ?? '',
    supplier: item?.supplier || '',
    location: item?.location || '',
    notes: item?.notes || '',
    ...saleFormFrom(item),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const update = (k, v) => {
    setForm((f) => {
      // When category changes (and item is new), auto-update unit default
      if (k === 'category' && !isEdit) {
        return { ...f, [k]: v, unit: defaultUnitForCategory(v) };
      }
      // Parts sold at the counter are counted in pieces, not square feet.
      if (k === 'usage' && !isEdit && v !== 'internal' && f.unit === defaultUnitForCategory(f.category)) {
        return { ...f, [k]: v, unit: 'nos' };
      }
      return { ...f, [k]: v };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const invalid = saleError(form);
    if (invalid) { setErr(invalid); return; }
    setErr('');
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        sku: form.sku,
        unit: form.unit,
        safety_stock: Number(form.safety_stock),
        supplier: form.supplier,
        location: form.location,
        notes: form.notes,
        ...salePayload(form),
      };
      // Unit cost is optional; only send when explicitly set (allows leaving blank)
      if (form.unit_cost !== '' && form.unit_cost !== null && form.unit_cost !== undefined) {
        const parsed = Number(form.unit_cost);
        if (!Number.isNaN(parsed) && parsed >= 0) payload.unit_cost = parsed;
      }
      if (isEdit) {
        await axios.put(`${API}/inventory/${item.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/inventory`, { ...payload, quantity: Number(form.quantity) }, { headers: authHeader() });
      }
      onSaved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold" data-testid="item-dialog-title">{isEdit ? 'Edit Item' : 'Add New Item'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="item-dialog-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Name" required>
            <input required value={form.name} onChange={(e) => update('name', e.target.value)} className={inputCls} data-testid="form-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Section">
              <select value={form.category} onChange={(e) => update('category', e.target.value)} className={inputCls} data-testid="form-category">
                {sections.map((sec) => (
                  <option key={sec.key} value={sec.key}>{sec.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Part number / SKU">
              <input value={form.sku} onChange={(e) => update('sku', e.target.value)} className={inputCls} data-testid="form-sku" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {!isEdit && (
              <Field label="Initial Qty">
                <input type="number" min="0" step="0.01" value={form.quantity} onChange={(e) => update('quantity', e.target.value)} className={inputCls} data-testid="form-quantity" />
              </Field>
            )}
            <Field label="Unit">
              <select value={form.unit} onChange={(e) => update('unit', e.target.value)} className={inputCls} data-testid="form-unit">
                {!UNIT_OPTIONS.includes(form.unit) && <option value={form.unit}>{form.unit}</option>}
                {UNIT_OPTIONS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field label="Reorder level">
              <input type="number" min="0" step="0.01" value={form.safety_stock} onChange={(e) => update('safety_stock', e.target.value)} className={inputCls} data-testid="form-safety" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplier">
              <input value={form.supplier} onChange={(e) => update('supplier', e.target.value)} className={inputCls} data-testid="form-supplier" />
            </Field>
            <Field label="Rack / bin">
              <input value={form.location} onChange={(e) => update('location', e.target.value)} className={inputCls} data-testid="form-location" />
            </Field>
          </div>
          <Field label="Last unit cost (₹, optional) — drives valuation & COGS reports">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.unit_cost}
              onChange={(e) => update('unit_cost', e.target.value)}
              className={inputCls}
              placeholder="e.g. 150.50 per sq ft"
              data-testid="form-unit-cost"
            />
          </Field>
          <SaleFields form={form} update={update} />
          <Field label="Notes">
            <textarea rows="2" value={form.notes} onChange={(e) => update('notes', e.target.value)} className={inputCls} data-testid="form-notes" />
          </Field>

          {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="form-error">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" data-testid="form-cancel">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:bg-teal-700 text-slate-950 font-semibold rounded-lg flex items-center gap-2" data-testid="form-submit">
              {submitting && <Loader2 className="animate-spin" size={14} />}
              {isEdit ? 'Save Changes' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemDialog;
